import React from 'react';
import PropTypes from 'prop-types';
import Fab from '@material-ui/core/Fab';
import TableCell from '@material-ui/core/TableCell';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Skeleton from '@material-ui/lab/Skeleton';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import OkIcon from '@material-ui/icons/Check';
import EditIcon from '@material-ui/icons/Edit';
import CancelIcon from '@material-ui/icons/Cancel';

const NodeType = PropTypes.shape({
  sortedChildren: PropTypes.arrayOf(PropTypes.shape({})),
  values: PropTypes.arrayOf(PropTypes.any),
  treeDepth: PropTypes.number,
  collapsed: PropTypes.bool,
  obj: PropTypes.shape({}),
});

export default (col, colIndex, options) => {
  //
  // Header renderer
  //
  const {
    configuration,
    tableHandler,
    canCreate,
    canDelete,
    headerButtons: HeaderButtons,
  } = options;

  const AddRowIcon = configuration.add && configuration.add.icon || AddIcon;
  const addTooltip = configuration.add && configuration.add.tooltip
      && tableHandler.getIcon(configuration.add.icon)
      || tableHandler.getString('addNewRow', 'Add new row');
  const DeleteRowIcon = configuration.delete && configuration.delete.icon
      && tableHandler.getIcon(configuration.delete.icon)
      || DeleteIcon;
  const deleteTooltip = configuration.delete && configuration.delete.tooltip
          || tableHandler.getString('deleteRows', `Delete checked ${tableHandler.getString('rowOrRows', 'row(s)')}`);

  const makeHeaderRenderer = (tableProps) => {
    const { editedRow, checked } = tableProps;
    const hasCheckedSelection = () => {
      return checked.length > 0;
    };

    const Cell = ({ style }) => (
      <TableCell
        key="edit"
        padding="checkbox"
        className="tuc-table-edit-column"
        component="div"
        style={style}
      >
        <div className="tuc-button-row">
          {canCreate && !editedRow && (
            <Tooltip title={addTooltip}>
              <Fab
                color="secondary"
                aria-label="Add"
                onClick={tableHandler.onCreate}
                id="grb-table-header-create-button"
              >
                <AddRowIcon />
              </Fab>
            </Tooltip>
          )}
          {canDelete && !editedRow && (
            <Tooltip title={deleteTooltip}>
              <div>
                {/* div to avoid material-ui warning about having a tooltip on an inactive button */}
                <Fab
                  color="primary"
                  aria-label="Delete"
                  onClick={tableHandler.onDelete}
                  disabled={!hasCheckedSelection()}
                  id="grb-table-header-delete-button"
                >
                  <DeleteRowIcon />
                </Fab>
              </div>
            </Tooltip>
          )}
          {HeaderButtons && (
            <HeaderButtons {...tableProps} />
          )}
        </div>
      </TableCell>
    );

    Cell.propTypes = {
      style: PropTypes.shape({}),
    };
    Cell.defaultProps = {
      style: undefined,
    };
    return Cell;
  };

  //
  // Cell Renderer
  //

  const cellDataGetter = ({ rowData }) => rowData;

  const makeCellRenderer = (tableProps) => {
    const Cell = ({ rowData, style }) => {
      const editing = tableProps.editedRow === rowData;
      const errors = editing ? tableProps.validationErrors : undefined;
      const hasErrors = errors && !!errors.find(e => e);
      const { canEdit, showActionButtons, rowButtons: RowButtons } = tableProps;
      function handleStopEditing() {
        tableHandler.onStopEditing(rowData.obj);
      }
      function handleStartEditing() {
        tableHandler.onStartEditing(rowData.obj);
      }
      function handleCancelEditing() {
        tableHandler.onCancelEditing(rowData.obj);
      }
      if (!rowData) {
        return (
          <TableCell
            key="editing"
            padding="checkbox"
            className="tuc-table-edit-column"
            component="div"
          >
            <Skeleton variant="text" />
          </TableCell>
        );
      }
      return (
        <TableCell
          key="editing"
          padding="checkbox"
          className="tuc-table-edit-column"
          component="div"
          style={style}
        >
          <div className="tuc-button-row">
            {editing && (
              <Tooltip title="Validate">
                <div>
                  <IconButton
                    aria-label="Validate"
                    className="tuc-table-edit-row-button"
                    disabled={hasErrors}
                    onClick={handleStopEditing}
                  >
                    <OkIcon />
                  </IconButton>
                </div>
              </Tooltip>
            )}
            {canEdit && showActionButtons && (
              <Tooltip title={tableHandler.getString('editRow', 'Edit row')}>
                <IconButton aria-label="Edit" className="tuc-table-edit-row-button" onClick={handleStartEditing}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            {editing && (
              <Tooltip title="Cancel editing">
                <IconButton
                  aria-label="Cancel"
                  className="tuc-table-edit-row-button"
                  onClick={handleCancelEditing}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            )}
            {!editing && RowButtons && (
              <RowButtons {...tableProps} row={rowData.obj} />
            )}
          </div>
        </TableCell>
      );
    };

    Cell.propTypes = {
      rowData: NodeType.isRequired,
      style: PropTypes.shape({}),
    };

    Cell.defaultProps = {
      style: undefined,
    };

    return Cell;
  };

  const width = col && col.width || 100;
  const sizing = {
    width,
    minWidth: col && col.minWidth || width,
    maxWidth: width,
    flexShrink: 0,
    flexGrow: 0,
  };

  return {
    name: 'edit', // debug
    minWidth: () => sizing.minWidth,
    generateProps: (props) => ({
      key: 'editCol',
      ...sizing,
      headerRenderer: makeHeaderRenderer(props),
      cellRenderer: makeCellRenderer(props),
      cellDataGetter,
      dataKey: colIndex,
    }),
  };
};
